// components
import GridPageAbstract from '../abstract/GridPageAbstract';
import { mergeHeadWithStorage } from '../../utils/gridHeadStorage';
import { GATEWAY_API_KIND } from '../../constants/gatewayApiKind';
import { hasError } from '../../utils/formatError';
import { apiServesKind, schemaFor, settingsProblems, showsLink } from '../../utils/gatewaySettings';
import i18n from '../../i18n';

/**
 * Gateway settings: one gateway API configured for one kind — how the gateways
 * linking the row start a payin or payout, or where its result comes from. A
 * gateway links up to one row of each kind (GatewaysPage), and several gateways may
 * share one row.
 *
 * Kind and API are fixed once the row exists (TRIGGER_GATEWAY_SETTINGS_KIND_FIXED):
 * another kind or API is another row, so both are read-only on edit. A row a live
 * gateway links cannot be archived (TRIGGER_GATEWAY_SETTINGS_IN_USE).
 *
 * Admins only: the settings hold bank logins and tokens. They come back with every
 * secret masked, and a mask sent back keeps the stored secret.
 */
export default class GatewaySettingsPage extends GridPageAbstract {
    constructor(props) {
        super(props);
        this.Name = 'publicGatewaySettings';
        this.Server = this.props.context.getState().server.publicGatewaySettings;
        this.MenuDisplayType = 'menu';
        this.searchFields = ['name'];

        // The link a row of each API reads beside its settings, shown only for that
        // API (utils/gatewaySettings, SETTINGS_LINKS): one hidden is cleared on the
        // next change, so it is never sent for an API that does not read it.
        const link = (id) => (data) => showsLink(id, data?.gatewayApiId, this.apis());

        const defaultHead = [
            { id: 'name', required: true, label: 'name', alignRight: false, cell: 'Text', displayGrid: true },
            {
                id: 'kind',
                required: true,
                label: 'kind',
                alignRight: false,
                cell: 'Items',
                items: GATEWAY_API_KIND,
                displayOnEditForReadOnly: true,
                helperText: 'gatewaySettingsKind',
                displayGrid: true
            },
            // Offers the APIs that serve the kind chosen; changing the kind clears
            // one that does not (DialogAbstract.pruneHiddenFields).
            {
                id: 'gatewayApiId',
                required: true,
                label: 'gatewayApi',
                alignRight: false,
                cell: 'Select',
                from: 'publicGatewayApis',
                object: 'gatewayApi',
                show: 'name',
                optionsFilter: (api, data) => apiServesKind(api, data.kind),
                displayOnEditForReadOnly: true,
                helperText: 'gatewaySettingsApi',
                displayGrid: true
            },
            // Several tg_bot rows may share one bot, each with its own chat_id in
            // the settings: Telegram allows one webhook URL per bot, so the bot is
            // its own row.
            {
                id: 'tgBotId',
                label: 'tgBot',
                alignRight: false,
                cell: 'Select',
                from: 'publicTgBots',
                object: 'tgBot',
                show: 'name',
                showIfDialog: link('tgBotId'),
                helperText: 'gatewaySettingsTgBot',
                displayGrid: true
            },
            // The chat with the bank's bot whose messages decide the payins or
            // payouts; it implies the session. One live row per chat and kind.
            {
                id: 'tgSessionChatId',
                label: 'tgSessionChat',
                alignRight: false,
                cell: 'Select',
                from: 'publicTgSessionChats',
                object: 'tgSessionChat',
                show: 'tgSessionName+title',
                showIfDialog: link('tgSessionChatId'),
                helperText: 'gatewaySettingsTgSessionChat',
                displayGrid: true
            },
            // The proxies the browser jobs go out through, a random working one per
            // job. Empty means directly.
            {
                id: 'proxyListId',
                label: 'proxyList',
                alignRight: false,
                cell: 'Select',
                from: 'publicProxyLists',
                object: 'proxyList',
                show: 'name',
                showIfDialog: link('proxyListId'),
                helperText: 'gatewaySettingsProxyList',
                displayGrid: true
            },
            { id: 'isArchived', label: 'isArchived', alignRight: false, cell: 'Boolean', displayGrid: true },
            // The Json editor plus a Form of the settings the API's driver reads for
            // the kind, as core describes them, and Scenarios for an xscraper row
            // without one. Not required: an add without settings stores {}, and an
            // update without them keeps what is stored — which is what a replica
            // core has not described the drivers to returns them as (null).
            // optionsFrom, not from, loads the scenarios: `from` would drop the
            // whole field for a role that cannot read scraper scenarios.
            {
                id: 'settings',
                label: 'settings',
                alignRight: false,
                cell: 'GatewaySettings',
                optionsFrom: 'publicScraperScenarios',
                displayGrid: true,
                disableSort: true,
                helperText: 'gatewaySettingsJson',
                fullRow: true,
                validate: (value, values) => this.settingsProblem(value, values)
            },
            {
                id: 'modifiedReason',
                label: 'modifiedReason',
                alignRight: false,
                cell: 'BigText',
                displayGrid: true,
                disableEditor: true
            },
            {
                id: 'modifiedBy',
                label: 'modifiedBy',
                alignRight: false,
                cell: 'Select',
                object: 'modifiedBy',
                show: 'firstName+lastName',
                displayGrid: true,
                disableEditor: true,
                customSort: 'modifiedBy.firstName'
            },
            {
                id: 'dateCreatedModified',
                elements: ['dateCreated', 'dateModified'],
                label: 'dateCreatedModified',
                alignRight: false,
                cell: 'CreateModify',
                disableEditor: true,
                displayGrid: true,
                customSort: 'dateCreated'
            }
        ];
        this.Head = mergeHeadWithStorage(this.Name, defaultHead);

        this.state = {
            ...this.state,
            head: this.Head
        };
    }

    async componentDidMount() {
        this.loadSettingsSchemas();
        await super.componentDidMount();
    }

    /** The gateway APIs' light rows, loaded for the API select: their names and kinds. */
    apis = () => this.props.context.getState().controllersData?.publicGatewayApis;

    /**
     * Each driver's settings, once per session, into the store, where the settings
     * editor reads them. Without them it is the Json editor, and the cabinet still
     * checks what is saved.
     */
    loadSettingsSchemas = async () => {
        const { context } = this.props;
        if (context.getState().gatewaySettingsSchemas) return;
        try {
            const result = await this.Server.schemas();
            if (!hasError(result) && Array.isArray(result.data)) {
                context.update({ gatewaySettingsSchemas: result.data });
            }
        } catch (error) {
            console.error('Gateway settings schemas could not be loaded:', error);
        }
    };

    /** The settings rule: true, or that the driver's schema refuses them. */
    settingsProblem = (value, values) => {
        const { gatewaySettingsSchemas } = this.props.context.getState();
        const schema = schemaFor(values?.kind, values?.gatewayApiId, this.apis(), gatewaySettingsSchemas);
        // Absent and unparsed values are the Json rule's to report.
        if (!schema || value === null || value === undefined || typeof value === 'string') return true;
        return settingsProblems(schema, value).length === 0 || i18n.t('gatewaySettings.errors.fixFields');
    };
}
